import Flatpicker from '@/components/Flatpicker';
import { SelectFormInput } from '@/components/form';
import { hotelEngineService } from '@/api/services';
import { useHotelEngineSell } from '@/hooks';
import HotelSellClosedNotice from '@/views/hotels/components/HotelSellClosedNotice';
import { useEffect, useMemo, useState } from 'react';
import { Button, Card, Col, Dropdown, DropdownDivider, DropdownMenu, DropdownToggle, FormLabel, Row, Spinner } from 'react-bootstrap';
import { BsCalendar, BsDashCircle, BsGeoAlt, BsPerson, BsPlusCircle, BsSearch } from 'react-icons/bs';
import { useNavigate } from '@/compat/react-router-dom';

const toYmd = (value) => {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const AvailabilityFilter = () => {
  const navigate = useNavigate();
  const { sellable, loading: sellLoading, message: closedMessage } = useHotelEngineSell();
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!sellable) {
      setDestinations([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await hotelEngineService.getDestinations();
        const rows = res?.data?.data || res?.data || [];
        if (!cancelled) setDestinations(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (!cancelled) {
          setDestinations([]);
          setError(e?.response?.data?.message || e?.message || 'Failed to load destinations.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sellable]);

  const sortedDestinations = useMemo(
    () =>
      [...destinations].sort((a, b) =>
        String(a.hbx_name || a.hbx_code || '').localeCompare(String(b.hbx_name || b.hbx_code || ''), undefined, { sensitivity: 'base' })
      ),
    [destinations]
  );

  const selectKey = useMemo(
    () => (loading && sortedDestinations.length === 0 ? 'loading' : `dest-${sortedDestinations.map((d) => d.hbx_code).join('-')}`),
    [loading, sortedDestinations]
  );

  const [destinationCode, setDestinationCode] = useState('');
  const [formValue, setFormValue] = useState({
    stayFor: [new Date(), new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)],
    guests: {
      adults: 2,
      rooms: 1,
      children: 0
    }
  });

  const updateGuests = (type, increase = true) => {
    const val = formValue.guests[type];
    setFormValue({
      ...formValue,
      guests: {
        ...formValue.guests,
        [type]: increase ? val + 1 : val > 1 ? val - 1 : 0
      }
    });
  };

  const getGuestsValue = () => {
    let value = '';
    const guests = formValue.guests;
    if (guests.adults) {
      value += guests.adults + (guests.adults > 1 ? ' Adults ' : ' Adult ');
    }
    if (guests.children) {
      value += guests.children + (guests.children > 1 ? ' Children ' : ' Child ');
    }
    if (guests.rooms) {
      value += guests.rooms + (guests.rooms > 1 ? ' Rooms ' : ' Room ');
    }
    return value;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!sellable) return;
    const range = formValue.stayFor || [];
    const checkIn = toYmd(range[0]);
    const checkOut = toYmd(range[1] || range[0]);
    const params = new URLSearchParams({
      check_in: checkIn,
      check_out: checkOut && checkOut !== checkIn ? checkOut : '',
      adults: String(formValue.guests.adults || 2),
      children: String(formValue.guests.children || 0),
      rooms: String(formValue.guests.rooms || 1),
    });
    if (destinationCode) params.set('destination_code', destinationCode);
    if (!params.get('check_out') && checkIn) {
      const next = new Date(`${checkIn}T12:00:00`);
      next.setDate(next.getDate() + 3);
      params.set('check_out', toYmd(next));
    }
    navigate(`/hotels/search?${params.toString()}`);
  };

  if (sellLoading) {
    return (
      <Row>
        <Col xl={10} className="position-relative mt-n3 mt-xl-n9">
          <div className="d-flex align-items-center gap-2 text-muted py-3">
            <Spinner animation="border" size="sm" />
            <span className="small">Checking live rates…</span>
          </div>
        </Col>
      </Row>
    );
  }

  if (!sellable) {
    return (
      <Row>
        <Col xl={10} className="position-relative mt-n3 mt-xl-n9">
          <HotelSellClosedNotice message={closedMessage} />
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      <Col xl={10} className="position-relative mt-n3 mt-xl-n9">
        <h6 className="d-none d-xl-block mb-3">Check Availability</h6>

        <Card as="form" className="shadow rounded-3 position-relative p-4 pe-md-5 pb-5 pb-md-4" onSubmit={handleSubmit}>
          <Row className="g-4 align-items-center">
            <Col lg={4}>
              <div className="form-control-border form-control-transparent form-fs-md flex-centered gap-2">
                <BsGeoAlt size={37} />

                <div className="flex-grow-1">
                  <FormLabel className="form-label">Area</FormLabel>
                  {loading && sortedDestinations.length === 0 ? (
                    <div className="d-flex align-items-center gap-2 text-muted py-2">
                      <Spinner animation="border" size="sm" />
                      <span className="small">Loading destinations…</span>
                    </div>
                  ) : (
                    <SelectFormInput
                      key={selectKey}
                      className="form-select js-choice"
                      data-search-enabled="true"
                      value={destinationCode || ''}
                      onChange={(v) => setDestinationCode(v && v !== '-1' ? String(v) : '')}
                    >
                      <option value="">East Crete — all sale areas</option>
                      {sortedDestinations.map((d) => (
                        <option key={d.hbx_code} value={String(d.hbx_code)}>
                          {d.hbx_name || d.hbx_code}
                        </option>
                      ))}
                    </SelectFormInput>
                  )}
                  {error && (
                    <span className="text-danger small d-block mt-1">{error}</span>
                  )}
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="flex-centered">
                <div>
                  <BsCalendar size={37} className=" me-2" />
                </div>

                <div className="form-control-border form-control-transparent form-fs-md">
                  <FormLabel className="form-label">Check in - out</FormLabel>
                  <Flatpicker
                    value={formValue.stayFor}
                    getValue={(val) => {
                      setFormValue({
                        ...formValue,
                        stayFor: val
                      });
                    }}
                    options={{
                      mode: 'range',
                      dateFormat: 'd M',
                      closeOnSelect: false
                    }}
                  />
                </div>
              </div>
            </Col>

            <Col lg={4}>
              <div className="form-control-border form-control-transparent form-fs-md flex-centered">
                <div>
                  <BsPerson size={37} className=" me-2" />
                </div>

                <div className="w-100">
                  <label className="form-label">Guests &amp; rooms</label>
                  <Dropdown className="guest-selector me-2">
                    <DropdownToggle as="input" className="form-guest-selector form-control selection-result" value={getGuestsValue()} onChange={() => {}} />

                    <DropdownMenu className="guest-selector-dropdown" renderOnMount>
                      <li className="d-flex justify-content-between">
                        <div>
                          <h6 className="mb-0">Adults</h6>
                          <small>Ages 13 or above</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <Button variant="link" className="adult-remove p-0 mb-0" onClick={() => updateGuests('adults', false)}>
                            <BsDashCircle className=" fs-5 fa-fw" />
                          </Button>
                          <h6 className="guest-selector-count mb-0 adults">{formValue.guests.adults ?? 0}</h6>
                          <Button variant="link" className="adult-add p-0 mb-0" onClick={() => updateGuests('adults')}>
                            <BsPlusCircle className=" fs-5 fa-fw" />
                          </Button>
                        </div>
                      </li>

                      <DropdownDivider />

                      <li className="d-flex justify-content-between">
                        <div>
                          <h6 className="mb-0">Children</h6>
                          <small>Ages 13 below</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <Button variant="link" type="button" className="btn btn-link child-remove p-0 mb-0" onClick={() => updateGuests('children', false)}>
                            <BsDashCircle className="  fs-5 fa-fw" />
                          </Button>
                          <h6 className="guest-selector-count mb-0 child">{formValue.guests.children ?? 0}</h6>
                          <Button variant="link" type="button" className="btn btn-link child-add p-0 mb-0" onClick={() => updateGuests('children')}>
                            <BsPlusCircle className=" fs-5 fa-fw" />
                          </Button>
                        </div>
                      </li>

                      <DropdownDivider />

                      <li className="d-flex justify-content-between">
                        <div>
                          <h6 className="mb-0">Rooms</h6>
                          <small>Max room 8</small>
                        </div>
                        <div className="hstack gap-1 align-items-center">
                          <Button variant="link" type="button" className="room-remove p-0 mb-0" onClick={() => updateGuests('rooms', false)}>
                            <BsDashCircle className=" fs-5 fa-fw" />
                          </Button>
                          <h6 className="guest-selector-count mb-0 rooms">{formValue.guests.rooms ?? 0}</h6>
                          <Button variant="link" type="button" className="btn btn-link room-add p-0 mb-0" onClick={() => updateGuests('rooms')}>
                            <BsPlusCircle className=" fs-5 fa-fw" />
                          </Button>
                        </div>
                      </li>
                    </DropdownMenu>
                  </Dropdown>
                </div>
              </div>
            </Col>
          </Row>

          <div className="btn-position-md-middle">
            <button type="submit" className="icon-lg btn btn-round btn-primary mb-0 flex-centered">
              <BsSearch className=" fa-fw" />
            </button>
          </div>
        </Card>
      </Col>
    </Row>
  );
};

export default AvailabilityFilter;
